import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Package, Calculator, CheckCircle, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import { useSuperAdminCompany } from '../contexts/SuperAdminCompanyContext';
import CompanySelect from '../components/CompanySelect';

interface Client {
  id: number;
  name: string;
  isActive: boolean;
}

interface ClientAddress {
  id: number;
  clientId: number;
  type: 'shipping' | 'billing';
  address: string;
  isDefault: boolean;
  isActive: boolean;
}

interface Quality {
  id: number;
  name: string;
  isActive: boolean;
}

interface TakaStock {
  id: number;
  qualityId: number;
  totalMeters: number;
  grade?: string;
  status: string;
  quality: {
    id: number;
    name: string;
  };
}

interface SaleForm {
  clientId: number;
  qualityId: number;
  hsnCode: string;
  deliveryAddressId?: number;
  customDeliveryAddress?: string;
  tenantId?: number | null;
  items: {
    takaIds?: number[];
    totalMetersPerColumn?: number;
    columns?: number;
  }[];
}

const CreateSale: React.FC = () => {
  const navigate = useNavigate();
  const [saleMode, setSaleMode] = useState<'taka' | 'meter'>('taka');
  const [selectedQualityId, setSelectedQualityId] = useState<number>(0);
  const [selectedTakaIds, setSelectedTakaIds] = useState<number[]>([]);
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const { scope } = useSuperAdminCompany();
  const showCompanyPicker = user?.role === 'SUPERADMIN' && scope.mode === 'all';
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<SaleForm>({
    defaultValues: {
      clientId: 0,
      qualityId: 0,
      hsnCode: '',
      deliveryAddressId: 0,
      customDeliveryAddress: '',
      items: [{}],
    },
  });

  const [metersPerColumn, setMetersPerColumn] = useState<number>(0);
  const [autoSelectedTakaIds, setAutoSelectedTakaIds] = useState<number[]>([]);
  const [takaSearchTerm, setTakaSearchTerm] = useState<string>('');
  const [gradeFilter, setGradeFilter] = useState<string>('');
  const [useCustomAddress, setUseCustomAddress] = useState<boolean>(false);

  // Fetch clients for dropdown
  const { data: clientsResponse } = useQuery({
    queryKey: ['clients'],
    queryFn: () => apiService.get('/clients'),
  });
  
  const clients = clientsResponse?.clients || [];

  // Fetch client addresses when client is selected
  const watchedClientId = watch('clientId');
  const { data: clientAddressesResponse } = useQuery({
    queryKey: ['client-addresses', watchedClientId],
    queryFn: () => apiService.get(`/client-addresses/client/${watchedClientId}`),
    enabled: watchedClientId > 0,
  });

  const clientAddresses = clientAddressesResponse?.addresses || [];
  const shippingAddresses = clientAddresses.filter((addr: ClientAddress) => addr.type === 'shipping' && addr.isActive);

  // Fetch qualities for dropdown
  const { data: qualitiesResponse } = useQuery({
    queryKey: ['qualities'],
    queryFn: () => apiService.get('/qualities'),
  });
  
  const qualities = qualitiesResponse?.qualities || [];

  // Fetch available taka stocks
  const { data: takaStocksResponse, refetch: refetchTakaStocks } = useQuery({
    queryKey: ['available-taka-stocks', selectedQualityId],
    queryFn: () => apiService.get(`/sales/available-taka-stocks${selectedQualityId ? `?qualityId=${selectedQualityId}` : ''}`),
    enabled: selectedQualityId > 0,
  });

  const takaStocks = takaStocksResponse?.takaStocks || [];

  // Filter takas based on search and grade
  const filteredTakaStocks = React.useMemo(() => {
    return takaStocks.filter((taka: TakaStock) => {
      const matchesSearch = takaSearchTerm === '' || 
        taka.id.toString().includes(takaSearchTerm) ||
        taka.totalMeters.toString().includes(takaSearchTerm);
      const matchesGrade = gradeFilter === '' || taka.grade === gradeFilter;
      return matchesSearch && matchesGrade;
    });
  }, [takaStocks, takaSearchTerm, gradeFilter]);

  // Get unique grades for filter
  const availableGrades = React.useMemo(() => {
    const grades = new Set(takaStocks.map((taka: TakaStock) => taka.grade).filter(Boolean));
    return Array.from(grades) as string[];
  }, [takaStocks]);

  // Reset selections when mode changes
  React.useEffect(() => {
    if (saleMode === 'taka') {
      setAutoSelectedTakaIds([]);
      setMetersPerColumn(0);
    } else if (saleMode === 'meter') {
      setSelectedTakaIds([]);
    }
  }, [saleMode]);

  // Create sale mutation
  const createMutation = useMutation({
    mutationFn: (data: SaleForm) => apiService.post('/sales', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['taka-stock'] });
      toast.success('Sale created successfully!');
      setSelectedTenantId(null);
      navigate('/sales');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create sale');
    },
  });

  const handleQualityChange = (qualityId: number) => {
    setSelectedQualityId(qualityId);
    setSelectedTakaIds([]);
    if (saleMode === 'taka' && qualityId > 0) {
      refetchTakaStocks();
    }
  };

  const handleTakaSelection = (takaId: number) => {
    if (saleMode === 'taka') {
      setSelectedTakaIds(prev => 
        prev.includes(takaId) 
          ? prev.filter(id => id !== takaId)
          : [...prev, takaId]
      );
    } else {
      setAutoSelectedTakaIds(prev => 
        prev.includes(takaId) 
          ? prev.filter(id => id !== takaId)
          : [...prev, takaId]
      );
    }
  };

  const onSubmit = (data: SaleForm) => {
    if (!data.clientId) {
      toast.error('Please select a client');
      return;
    }

    if (!data.qualityId) {
      toast.error('Please select a quality');
      return;
    }

    if (!data.hsnCode.trim()) {
      toast.error('Please enter HSN Code');
      return;
    }

    if (showCompanyPicker && !selectedTenantId) {
      toast.error('Please select a company');
      return;
    }

    let validItems: any[] = [];
    let takaIdsToUse: number[] = [];

    if (saleMode === 'taka') {
      if (selectedTakaIds.length === 0) {
        toast.error('Please select at least one taka');
        return;
      }
      takaIdsToUse = selectedTakaIds;
    } else if (saleMode === 'meter') {
      if (!metersPerColumn || metersPerColumn <= 0) {
        toast.error('Please enter meters per column');
        return;
      }
      if (autoSelectedTakaIds.length === 0) {
        toast.error('Please select at least one taka');
        return;
      }
      takaIdsToUse = autoSelectedTakaIds;
    }

    validItems = [{
      qualityId: data.qualityId,
      takaIds: takaIdsToUse
    }];

    createMutation.mutate({
      clientId: data.clientId,
      qualityId: data.qualityId,
      hsnCode: data.hsnCode,
      items: validItems,
      tenantId: showCompanyPicker ? selectedTenantId : undefined,
    });
  };

  const calculateTotalMeters = () => {
    const takaIdsToUse = saleMode === 'taka' ? selectedTakaIds : autoSelectedTakaIds;
    return takaStocks
      .filter((taka: TakaStock) => takaIdsToUse.includes(taka.id))
      .reduce((sum: number, taka: TakaStock) => sum + taka.totalMeters, 0);
  };

  const totalMeters = calculateTotalMeters();

  const generatePDF = () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString('en-GB');
    const selectedClient = clients.find((c: Client) => c.id === watch('clientId'));
    const selectedQuality = qualities.find((q: Quality) => q.id === watch('qualityId'));

    // Header
    doc.setFontSize(16);
    doc.text('|| Shree Ganeshay Namaha ||', 105, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.text('GANPATRAM KASHIRAM & CO.', 105, 30, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text('S.NO-7,PLOT NO 13/D,MAHAPRABHU NAGAR,LIMBAYAT,SURAT', 105, 35, { align: 'center' });
    doc.text('9377675883 / 9374706649', 105, 40, { align: 'center' });
    
    // GST Details
    doc.text('GST/ARN NO: 24AAHPT8213B1ZY', 20, 50);
    doc.text('PAN NO: AAHPT8213B', 20, 55);
    doc.text('TIN NO: 24221802027', 20, 60);

    // Challan Details
    doc.text(`CHALLAN NO: ${Math.floor(Math.random() * 1000) + 1}`, 150, 50);
    doc.text(`DATE: ${currentDate}`, 150, 55);
    doc.text(`HSNCODE: ${watch('hsnCode') || '0'}`, 150, 60);

    // To Section
    doc.text(`To: ${selectedClient?.name || 'N/A'}`, 20, 75);
    const deliveryAddr = useCustomAddress 
      ? watch('customDeliveryAddress')
      : shippingAddresses.find((addr: ClientAddress) => addr.id === watch('deliveryAddressId'))?.address || 'UDHNA SURAT';
    doc.text(`ADDRESS: ${deliveryAddr}`, 20, 80);

    // Quality Section (moved to top as requested)
    doc.text(`QUALITY: ${selectedQuality?.name || 'N/A'}`, 20, 90);

    // Table Header
    doc.setFontSize(12);
    doc.text('JOB WORK CHALLAN', 105, 110, { align: 'center' });

    // Table Structure
    const startY = 120;
    let currentY = startY;

    // Get the selected takas (either manually selected or auto-selected)
    const takaIdsToUse = saleMode === 'taka' ? selectedTakaIds : autoSelectedTakaIds;
    const selectedTakaList = takaStocks.filter((taka: TakaStock) => takaIdsToUse.includes(taka.id));

    if (selectedTakaList.length > 0) {
      
      // Table headers
      doc.setFontSize(10);
      doc.text('SR.NO.', 20, currentY);
      doc.text('Mtrs.', 40, currentY);
      doc.text('SR.NO.', 70, currentY);
      doc.text('Mtrs.', 90, currentY);
      doc.text('SR.NO.', 120, currentY);
      doc.text('Mtrs.', 140, currentY);
      doc.text('SR.NO.', 170, currentY);
      doc.text('Mtrs.', 190, currentY);
      
      currentY += 10;

      // Table data in 4 columns
      for (let i = 0; i < selectedTakaList.length; i += 4) {
        const taka1 = selectedTakaList[i];
        const taka2 = selectedTakaList[i + 1];
        const taka3 = selectedTakaList[i + 2];
        const taka4 = selectedTakaList[i + 3];

        if (taka1) {
          doc.text(`${i + 1}`, 20, currentY);
          doc.text(`${taka1.totalMeters.toFixed(2)}`, 40, currentY);
        }
        if (taka2) {
          doc.text(`${i + 2}`, 70, currentY);
          doc.text(`${taka2.totalMeters.toFixed(2)}`, 90, currentY);
        }
        if (taka3) {
          doc.text(`${i + 3}`, 120, currentY);
          doc.text(`${taka3.totalMeters.toFixed(2)}`, 140, currentY);
        }
        if (taka4) {
          doc.text(`${i + 4}`, 170, currentY);
          doc.text(`${taka4.totalMeters.toFixed(2)}`, 190, currentY);
        }
        
        currentY += 8;
      }
    }

    // Total
    currentY += 10;
    doc.text('TOTAL', 170, currentY);
    doc.text(`${totalMeters.toFixed(2)}`, 190, currentY);

    // Summary
    currentY += 20;
    doc.text(`TOTAL TAKA: ${selectedTakaList.length}`, 20, currentY);
    doc.text(`TOTAL MTR.: ${totalMeters.toFixed(2)}`, 105, currentY);

    // Signatures
    currentY += 20;
    doc.text('RECEIVER SIGN.', 20, currentY);
    doc.text('RECEIVER MOBILE NUMBER.', 20, currentY + 10);
    doc.text('For:- GANPATRAM KASHIRAM & CO.', 150, currentY);

    // Save the PDF
    doc.save(`challan-${selectedClient?.name?.replace(/\s+/g, '-')}-${currentDate}.pdf`);
    toast.success('PDF generated successfully!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/sales')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Sales
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <h1 className="text-xl font-semibold text-gray-900">Create New Sale</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">Sale Details</h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {showCompanyPicker && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company *
                    </label>
                    <CompanySelect value={selectedTenantId} onChange={setSelectedTenantId} />
                  </div>
                )}
                {/* Client Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To (Client) *
                  </label>
                  <select
                    {...register('clientId', { required: 'Client is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={0}>Select a client</option>
                    {clients.map((client: Client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {errors.clientId && (
                    <p className="mt-1 text-sm text-red-600">Client is required</p>
                  )}
                </div>

                {/* Delivery Address */}
                {watchedClientId > 0 && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Delivery Address
                    </label>
                    
                    {/* Address Selection Mode */}
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!useCustomAddress}
                          onChange={() => setUseCustomAddress(false)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Select saved address</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={useCustomAddress}
                          onChange={() => setUseCustomAddress(true)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Enter custom address</span>
                      </label>
                    </div>

                    {!useCustomAddress ? (
                      /* Saved Address Dropdown */
                      <select
                        {...register('deliveryAddressId')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={0}>Select delivery address</option>
                        {shippingAddresses.map((addr: ClientAddress) => (
                          <option key={addr.id} value={addr.id}>
                            {addr.address} {addr.isDefault ? '(Default)' : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      /* Custom Address Input */
                      <textarea
                        {...register('customDeliveryAddress')}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter custom delivery address for this sale..."
                      />
                    )}

                    {shippingAddresses.length === 0 && !useCustomAddress && (
                      <p className="text-sm text-amber-600">
                        ⚠️ No saved addresses found for this client. Enter a custom address or add addresses in Client Management.
                      </p>
                    )}
                  </div>
                )}

                {/* Quality and HSN Code Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quality *
                    </label>
                    <select
                      {...register('qualityId', { required: 'Quality is required' })}
                      onChange={(e) => handleQualityChange(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={0}>Select a quality</option>
                      {qualities.map((quality: Quality) => (
                        <option key={quality.id} value={quality.id}>
                          {quality.name}
                        </option>
                      ))}
                    </select>
                    {errors.qualityId && (
                      <p className="mt-1 text-sm text-red-600">Quality is required</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HSN Code *
                    </label>
                    <input
                      {...register('hsnCode', { required: 'HSN Code is required' })}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter HSN Code"
                    />
                    {errors.hsnCode && (
                      <p className="mt-1 text-sm text-red-600">HSN Code is required</p>
                    )}
                  </div>
                </div>

                {/* Sale Mode Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Sale Type *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setSaleMode('taka');
                        setMetersPerColumn(0);
                        setAutoSelectedTakaIds([]);
                      }}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        saleMode === 'taka'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <Package className={`h-5 w-5 mr-3 ${saleMode === 'taka' ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div>
                          <div className="font-medium text-gray-900">Taka Based</div>
                          <div className="text-sm text-gray-500">Select individual takas from stock</div>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setSaleMode('meter');
                        setSelectedTakaIds([]);
                      }}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        saleMode === 'meter'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <Calculator className={`h-5 w-5 mr-3 ${saleMode === 'meter' ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div>
                          <div className="font-medium text-gray-900">Meter Based</div>
                          <div className="text-sm text-gray-500">Select takas to calculate total meters</div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Taka Selection */}
                {saleMode === 'taka' && selectedQualityId > 0 && (
                  <div className="space-y-4">
                    {/* Search and Filter Bar */}
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Search by Taka ID or meters..."
                          value={takaSearchTerm}
                          onChange={(e) => setTakaSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      {availableGrades.length > 0 && (
                        <select
                          value={gradeFilter}
                          onChange={(e) => setGradeFilter(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">All Grades</option>
                          {availableGrades.map((grade: string) => (
                            <option key={grade} value={grade}>{grade}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Selection Actions */}
                    {filteredTakaStocks.length > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          Showing {filteredTakaStocks.length} of {takaStocks.length} takas
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const allIds = filteredTakaStocks.map((taka: TakaStock) => taka.id);
                              setSelectedTakaIds(allIds);
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Select All
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={() => setSelectedTakaIds([])}
                            className="text-red-600 hover:text-red-800 font-medium"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Taka List */}
                    <div className="border border-gray-200 rounded-lg">
                      <div className="max-h-96 overflow-y-auto">
                        {filteredTakaStocks.length === 0 ? (
                          <div className="p-6 text-center text-gray-500">
                            <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p>{takaSearchTerm || gradeFilter ? 'No matching takas found' : 'No taka available for this quality'}</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-200">
                            {filteredTakaStocks.map((taka: TakaStock) => (
                              <label
                                key={taka.id}
                                className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                                  selectedTakaIds.includes(taka.id) ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedTakaIds.includes(taka.id)}
                                  onChange={() => handleTakaSelection(taka.id)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <div className="ml-3 flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-medium text-gray-900">#{taka.id}</span>
                                      {taka.grade && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                          {taka.grade}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {taka.totalMeters.toFixed(2)}m
                                    </div>
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Selected Summary */}
                    {selectedTakaIds.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-blue-900">
                            ✓ {selectedTakaIds.length} taka(s) selected
                          </span>
                          <span className="text-sm font-bold text-blue-900">
                            {takaStocks
                              .filter((taka: TakaStock) => selectedTakaIds.includes(taka.id))
                              .reduce((sum: number, taka: TakaStock) => sum + taka.totalMeters, 0)
                              .toFixed(2)}m
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Meter Based Input */}
                {saleMode === 'meter' && selectedQualityId > 0 && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="text-sm text-blue-800">
                          <strong>Meter Based Mode:</strong> Enter meters per column, then select takas. The system will calculate columns and takas per column automatically.
                        </div>
                      </div>
                    </div>

                    {/* Meters Per Column Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meters per Column (±30m) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={metersPerColumn || ''}
                        onChange={(e) => setMetersPerColumn(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 2200"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter the total meters that should be in each column
                      </p>
                    </div>

                    {/* Search and Filter Bar */}
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Search by Taka ID or meters..."
                          value={takaSearchTerm}
                          onChange={(e) => setTakaSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      {availableGrades.length > 0 && (
                        <select
                          value={gradeFilter}
                          onChange={(e) => setGradeFilter(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">All Grades</option>
                          {availableGrades.map((grade: string) => (
                            <option key={grade} value={grade}>{grade}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Selection Actions */}
                    {filteredTakaStocks.length > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          Showing {filteredTakaStocks.length} of {takaStocks.length} takas
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const allIds = filteredTakaStocks.map((taka: TakaStock) => taka.id);
                              setAutoSelectedTakaIds(allIds);
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Select All
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={() => setAutoSelectedTakaIds([])}
                            className="text-red-600 hover:text-red-800 font-medium"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Taka Selection List */}
                    <div className="border border-gray-200 rounded-lg">
                      <div className="max-h-96 overflow-y-auto">
                        {filteredTakaStocks.length === 0 ? (
                          <div className="p-6 text-center text-gray-500">
                            <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p>{takaSearchTerm || gradeFilter ? 'No matching takas found' : 'No taka available for this quality'}</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-200">
                            {filteredTakaStocks.map((taka: TakaStock) => (
                              <label
                                key={taka.id}
                                className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                                  autoSelectedTakaIds.includes(taka.id) ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={autoSelectedTakaIds.includes(taka.id)}
                                  onChange={() => handleTakaSelection(taka.id)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <div className="ml-3 flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-medium text-gray-900">#{taka.id}</span>
                                      {taka.grade && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                          {taka.grade}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {taka.totalMeters.toFixed(2)}m
                                    </div>
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Selected Summary with Calculations */}
                    {autoSelectedTakaIds.length > 0 && metersPerColumn > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between pb-3 border-b border-green-200">
                            <div>
                              <div className="text-sm font-medium text-green-900">✓ {autoSelectedTakaIds.length} Taka(s) Selected</div>
                              <div className="text-xs text-green-700 mt-1">
                                Total meters: {calculateTotalMeters().toFixed(2)}m
                              </div>
                            </div>
                            <div className="text-2xl font-bold text-green-900">
                              {calculateTotalMeters().toFixed(2)}m
                            </div>
                          </div>
                          
                          {/* Column Calculation */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white rounded p-3 border border-green-200">
                              <div className="text-xs text-green-700 mb-1">Total Columns</div>
                              <div className="text-lg font-bold text-green-900">
                                {Math.ceil(calculateTotalMeters() / metersPerColumn)} columns
                              </div>
                            </div>
                            <div className="bg-white rounded p-3 border border-green-200">
                              <div className="text-xs text-green-700 mb-1">Takas per Column</div>
                              <div className="text-lg font-bold text-green-900">
                                {(autoSelectedTakaIds.length / Math.ceil(calculateTotalMeters() / metersPerColumn)).toFixed(1)} takas
                              </div>
                            </div>
                          </div>

                          {/* Calculation Formula */}
                          <div className="text-xs text-green-700 bg-white rounded p-2 border border-green-200">
                            📊 Calculation: {calculateTotalMeters().toFixed(0)}m ÷ {metersPerColumn.toFixed(0)}m/column = {Math.ceil(calculateTotalMeters() / metersPerColumn)} columns
                            <br />
                            {autoSelectedTakaIds.length} takas ÷ {Math.ceil(calculateTotalMeters() / metersPerColumn)} columns = {(autoSelectedTakaIds.length / Math.ceil(calculateTotalMeters() / metersPerColumn)).toFixed(1)} takas/column
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="pt-6 border-t space-y-3">
                  <button
                    type="button"
                    onClick={generatePDF}
                    disabled={!watch('clientId') || !watch('qualityId') || !watch('hsnCode') || totalMeters === 0}
                    className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Generate PDF Challan
                  </button>
                  
                  <button
                    type="submit"
                    disabled={createMutation.isPending || totalMeters === 0}
                    className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Creating Sale...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Create Sale
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-sm rounded-lg p-6 sticky top-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Sale Summary</h3>
              
              {watch('clientId') > 0 && watch('qualityId') > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-gray-700">Client:</span>
                    <span className="text-sm text-gray-900">
                      {clients.find((c: Client) => c.id === watch('clientId'))?.name || 'N/A'}
                    </span>
                  </div>

                  {((watch('deliveryAddressId') || 0) > 0 || watch('customDeliveryAddress')) && (
                    <div className="py-2 border-b">
                      <span className="text-sm font-medium text-gray-700">Delivery Address:</span>
                      <div className="text-sm text-gray-900 mt-1 whitespace-pre-line">
                        {useCustomAddress 
                          ? watch('customDeliveryAddress')
                          : shippingAddresses.find((addr: ClientAddress) => addr.id === watch('deliveryAddressId'))?.address || 'N/A'
                        }
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-gray-700">Quality:</span>
                    <span className="text-sm text-gray-900">
                      {qualities.find((q: Quality) => q.id === watch('qualityId'))?.name || 'N/A'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-gray-700">HSN Code:</span>
                    <span className="text-sm text-gray-900">
                      {watch('hsnCode') || 'N/A'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-gray-700">Sale Type:</span>
                    <span className="text-sm text-gray-900 capitalize">
                      {saleMode === 'taka' ? 'Taka Based' : 'Meter Based'}
                    </span>
                  </div>

                  {saleMode === 'taka' && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm font-medium text-gray-700">Selected Taka:</span>
                      <span className="text-sm text-gray-900">
                        {selectedTakaIds.length} item(s)
                      </span>
                    </div>
                  )}

                  {saleMode === 'meter' && (
                    <>
                      <div className="flex items-center justify-between py-2 border-b">
                        <span className="text-sm font-medium text-gray-700">Meters/Column:</span>
                        <span className="text-sm text-gray-900">
                          {metersPerColumn || 0}m
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b">
                        <span className="text-sm font-medium text-gray-700">Selected Takas:</span>
                        <span className="text-sm text-gray-900 font-semibold text-green-600">
                          {autoSelectedTakaIds.length} Taka(s)
                        </span>
                      </div>
                      {metersPerColumn > 0 && autoSelectedTakaIds.length > 0 && (
                        <>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-gray-700">Total Columns:</span>
                            <span className="text-sm text-gray-900 font-semibold text-blue-600">
                              {Math.ceil(calculateTotalMeters() / metersPerColumn)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-gray-700">Takas/Column:</span>
                            <span className="text-sm text-gray-900">
                              ~{(autoSelectedTakaIds.length / Math.ceil(calculateTotalMeters() / metersPerColumn)).toFixed(1)}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-base font-medium text-gray-900">Total Meters:</span>
                      <span className="text-xl font-bold text-blue-600">
                        {totalMeters.toFixed(2)}m
                      </span>
                    </div>
                  </div>

                  {(saleMode === 'taka' ? selectedTakaIds.length > 0 : autoSelectedTakaIds.length > 0) && (
                    <div className="mt-4">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        {saleMode === 'taka' ? 'Selected Items:' : 'Selected Items:'}
                      </div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {takaStocks
                          .filter((taka: TakaStock) => 
                            (saleMode === 'taka' ? selectedTakaIds : autoSelectedTakaIds).includes(taka.id)
                          )
                          .map((taka: TakaStock) => (
                            <div key={taka.id} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                              Taka #{taka.id}: {taka.totalMeters}m {taka.grade && `(${taka.grade})`}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {(!watch('clientId') || !watch('qualityId')) && (
                <div className="text-center text-gray-500 py-8">
                  <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Select client and quality to see summary</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSale;
